import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useClock } from "../hooks/useClock";
import { useGeolocation } from "../hooks/useGeolocation";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { createTimestamp, startAlcoholCheck } from "../services/api";
import { saveOfflineTimestamp } from "../services/offlineDb";
import { rawToAlcoholMgL, isAlcoholSentinelRaw } from "../services/alcohol-value";
import type { AuthUser } from "../hooks/useAuth";
import { CompanyLogo } from "../components/CompanyLogo";

interface Props { user: AuthUser; }

const ACTION_CONFIG: Record<string, {
  label: string; emoji: string; color: string[]; shadow: string; nextStatus: string;
}> = {
  clock_in:  { label: "業務開始",  emoji: "🟢", color: ["#10b981","#059669"], shadow: "rgba(16,185,129,0.45)", nextStatus: "working" },
  clock_out: { label: "業務終了",  emoji: "🔴", color: ["#ef4444","#dc2626"], shadow: "rgba(239,68,68,0.45)",  nextStatus: "idle"    },
};

export const TimestampPage: React.FC<Props> = ({ user: _user }) => {
  const [params] = useSearchParams();
  const action = params.get("action") ?? "clock_in";
  const { time, date, now } = useClock();
  const { position, getPosition, loading: geoLoading } = useGeolocation();
  const isOnline = useOnlineStatus();
  const nav = useNavigate();
  const [stamping, setStamping] = useState(false);
  const [done, setDone] = useState(false);
  const [pressed, setPressed] = useState(false);

  // アルコールチェック関連
  const [alcoholPhase, setAlcoholPhase] = useState<"idle"|"waiting"|"done"|"timeout">("idle");
  const [alcoholValue, setAlcoholValue] = useState<number | null>(null);

  const cfg = ACTION_CONFIG[action] ?? ACTION_CONFIG["clock_in"]!;

  useEffect(() => { getPosition(); }, [getPosition]);

  const doStamp = useCallback(async (alcoValue?: number) => {
    setStamping(true);
    const geo = position ?? (await getPosition());
    const recordedAt = now.toISOString();
    const sessionId = localStorage.getItem("catms_work_session_id");

    const payload: Record<string, unknown> = {
      timestamp_type: action,
      recorded_at: recordedAt,
      timestamp_source: "pwa",
      latitude: geo?.latitude ?? null,
      longitude: geo?.longitude ?? null,
      communication_type: isOnline ? "lte" : "offline",
      work_session_id: action !== "clock_in" ? sessionId : undefined,
      vessel_id: action === "clock_in" ? localStorage.getItem("catms_vessel_id") : undefined,
      alcohol_value: alcoValue ?? null,
      note: alcoValue != null ? `アルコールチェック: ${alcoValue.toFixed(3)} mg/L` : undefined,
    };

    try {
      if (isOnline) {
        const res = await createTimestamp(payload);
        if (action === "clock_in" && res) {
          const ws = res as Record<string, Record<string, unknown>>;
          localStorage.setItem("catms_work_session_id", String(ws["work_session"]?.["id"] ?? ""));
        }
      } else {
        await saveOfflineTimestamp({
          localId: crypto.randomUUID(),
          timestampType: action,
          recordedAt,
          latitude: geo?.latitude ?? null,
          longitude: geo?.longitude ?? null,
          timestampSource: "pwa",
          communicationType: "offline",
          workSessionId: sessionId,
          vesselId: localStorage.getItem("catms_vessel_id"),
          workType: null,
          note: null,
        });
      }
      localStorage.setItem("catms_session_status", cfg.nextStatus);
      if (action === "clock_out") localStorage.removeItem("catms_work_session_id");
      setDone(true);
      setTimeout(() => nav("/home"), 2000);
    } catch (err) {
      alert(err instanceof Error ? err.message : "打刻に失敗しました");
    } finally {
      setStamping(false);
    }
  }, [action, position, getPosition, now, isOnline, nav, cfg]);

  const handleStamp = useCallback(() => {
    if (action === "clock_in") {
      // 出勤打刻はアルコールチェックを先に実施
      setAlcoholPhase("waiting");
      startAlcoholCheck(
        () => {},                          // onWarming (unused in this page)
        () => setAlcoholPhase("waiting"),
        // 届くのは検知器の生バイト。mg/L への変換と低濃度域の丸めは共通処理に任せる。
        (raw: number) => {
          // 番兵値（呼気未検知コード）は結果にしない（誤って基準値超過扱いになるのを防ぐ）
          if (isAlcoholSentinelRaw(raw)) { setAlcoholPhase("timeout"); return; }
          const mgL = rawToAlcoholMgL(raw);
          setAlcoholValue(mgL);
          setAlcoholPhase("done");
          doStamp(mgL);
        },
        () => setAlcoholPhase("timeout"),
        () => setAlcoholPhase("timeout"),
        () => setAlcoholPhase("timeout"),  // no_breath → same as timeout for this page
      ).catch(() => setAlcoholPhase("timeout"));
    } else {
      doStamp();
    }
  }, [action, doStamp]);

  return (
    <div style={{
      flex: 1, display: "flex", flexDirection: "column",
      background: "#f8fafc", overflow: "hidden",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Hiragino Sans', sans-serif",
    }}>
      {/* ヘッダー */}
      <div className="app-header" style={{
        background: "white", borderBottom: "1px solid #e2e8f0",
        padding: "0 20px 14px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)", flexShrink: 0,
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <button onClick={() => nav("/home")} style={{
          background: "none", border: "none", cursor: "pointer",
          color: "#64748b", fontSize: 22, padding: "4px 8px",
          display: "flex", alignItems: "center",
        }}>←</button>
        <CompanyLogo height={26} />
      </div>

      {/* メインエリア */}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", padding: "24px",
      }}>
        {done ? (
          <div style={{ textAlign: "center" }}>
            <div style={{
              width: 120, height: 120, borderRadius: "50%",
              background: "linear-gradient(135deg, #10b981, #059669)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 24px",
              boxShadow: "0 0 0 16px rgba(16,185,129,0.12), 0 16px 48px rgba(16,185,129,0.4)",
            }}>
              <span style={{ fontSize: 52 }}>✅</span>
            </div>
            <p style={{ fontSize: 24, fontWeight: 800, color: "#0f172a", margin: "0 0 8px" }}>{cfg.label}完了</p>
            {alcoholValue !== null && (
              <p style={{ fontSize: 16, color: "#64748b", margin: "0 0 4px" }}>
                アルコール検査: <strong style={{ color: alcoholValue === 0 ? "#10b981" : "#ef4444" }}>{alcoholValue === 0 ? "異常なし" : `検出値: ${alcoholValue.toFixed(3)} mg/L`}</strong>
              </p>
            )}
            <p style={{ fontSize: 14, color: "#94a3b8", margin: 0 }}>ホームに戻ります...</p>
          </div>
        ) : alcoholPhase === "waiting" ? (
          /* アルコールチェック待機画面 */
          <div style={{ textAlign: "center" }}>
            <div style={{
              width: 120, height: 120, borderRadius: "50%",
              background: "linear-gradient(135deg, #3b82f6, #2563eb)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 24px",
              boxShadow: "0 0 0 16px rgba(59,130,246,0.12), 0 16px 48px rgba(59,130,246,0.4)",
            }}>
              <span style={{ fontSize: 52 }}>💨</span>
            </div>
            <p style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: "0 0 8px" }}>アルコールチェック中</p>
            <p style={{ fontSize: 14, color: "#64748b", margin: "0 0 24px" }}>AN-S126aを挿して息を吹きかけてください</p>
            <div style={{
              width: 36, height: 36,
              border: "3px solid rgba(59,130,246,0.3)",
              borderTopColor: "#3b82f6", borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              margin: "0 auto",
            }} />
          </div>
        ) : alcoholPhase === "timeout" ? (
          /* タイムアウト画面 */
          <div style={{ textAlign: "center" }}>
            <div style={{
              width: 120, height: 120, borderRadius: "50%",
              background: "linear-gradient(135deg, #ef4444, #dc2626)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 24px",
            }}>
              <span style={{ fontSize: 52 }}>⏱️</span>
            </div>
            <p style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: "0 0 8px" }}>タイムアウト</p>
            <p style={{ fontSize: 14, color: "#64748b", margin: "0 0 24px" }}>もう一度試してください</p>
            <button onClick={() => setAlcoholPhase("idle")} style={{
              background: "linear-gradient(135deg, #3b82f6, #2563eb)",
              color: "white", border: "none", borderRadius: 12,
              padding: "12px 32px", fontSize: 16, fontWeight: 700, cursor: "pointer",
            }}>再試行</button>
          </div>
        ) : (
          <>
            <div style={{ textAlign: "center", marginBottom: 40 }}>
              <p style={{ margin: "0 0 4px", fontSize: 18, color: "#1e293b", fontWeight: 700 }}>{date}</p>
              <p style={{ margin: 0, fontSize: 52, fontWeight: 900, color: "#0f172a", letterSpacing: "-2px", lineHeight: 1 }}>{time}</p>
            </div>

            <button
              onClick={handleStamp}
              disabled={stamping}
              onMouseDown={() => setPressed(true)}
              onMouseUp={() => setPressed(false)}
              onTouchStart={() => setPressed(true)}
              onTouchEnd={() => setPressed(false)}
              style={{
                width: 200, height: 200,
                borderRadius: "50%", border: "none",
                background: stamping
                  ? "linear-gradient(145deg, #94a3b8, #64748b)"
                  : `linear-gradient(145deg, ${cfg.color[0]}, ${cfg.color[1]})`,
                color: "white",
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                gap: 10, cursor: stamping ? "not-allowed" : "pointer",
                boxShadow: stamping ? "none" : `0 0 0 14px ${cfg.color[0]}20, 0 20px 60px ${cfg.shadow}`,
                transform: pressed ? "scale(0.95)" : "scale(1)",
                transition: "transform 0.12s, box-shadow 0.12s",
                marginBottom: 36,
              }}
            >
              {stamping ? (
                <>
                  <div style={{
                    width: 36, height: 36,
                    border: "3px solid rgba(255,255,255,0.3)",
                    borderTopColor: "white", borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                  }} />
                  <span style={{ fontSize: 15, fontWeight: 700 }}>処理中...</span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: 48 }}>{cfg.emoji}</span>
                  <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: "0.5px" }}>{cfg.label}</span>
                </>
              )}
            </button>

            <div style={{
              background: "white", borderRadius: 16,
              border: "1px solid #e2e8f0",
              padding: "16px 20px", width: "100%", maxWidth: 320,
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>GPS</span>
                <span style={{ fontSize: 12, color: geoLoading ? "#f59e0b" : position ? "#10b981" : "#ef4444", fontWeight: 700 }}>
                  {geoLoading ? "取得中..." : position ? `${position.latitude.toFixed(4)}, ${position.longitude.toFixed(4)}` : "取得不可"}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>通信</span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: isOnline ? "#10b981" : "#ef4444" }} />
                  <span style={{ fontSize: 12, color: isOnline ? "#10b981" : "#ef4444", fontWeight: 700 }}>
                    {isOnline ? "オンライン" : "オフライン"}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};